import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { ScrollText, Sparkles, Loader2, CheckCircle2, Wand2 } from "lucide-react";

const QUESTIONS = [
  { key: "returns", label: "Retour / remboursement", ph: "Ex : retour accepté sous 7 jours si l'article n'a pas été porté." },
  { key: "exchanges", label: "Échanges", ph: "Ex : échange possible pour une autre taille sous 7 jours." },
  { key: "delivery", label: "Délais de préparation et de livraison", ph: "Ex : préparation 24-48h, livraison Abidjan 2-3 jours." },
  { key: "non_returnable", label: "Produits non repris", ph: "Ex : sous-vêtements et articles soldés non repris." },
  { key: "payment", label: "Moyens de paiement acceptés", ph: "Ex : Wave, Orange Money, espèces à la livraison." },
  { key: "support", label: "Service client (contact, délai de réponse)", ph: "Ex : WhatsApp, réponse sous 24h." },
];

export function SalesPolicyCard() {
  const [answers, setAnswers] = useState({});
  const [fr, setFr] = useState("");
  const [en, setEn] = useState("");
  const [published, setPublished] = useState(false);
  const [lang, setLang] = useState("fr");
  const [genBusy, setGenBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);

  useEffect(() => {
    api.get("/business/sales-policy")
      .then(({ data }) => {
        setAnswers(data.answers || {});
        setFr(data.content?.fr || "");
        setEn(data.content?.en || "");
        setPublished(!!data.published);
      })
      .catch(() => {});
  }, []);

  const setA = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  const hasAnswers = QUESTIONS.some((q) => (answers[q.key] || "").trim());
  const text = lang === "fr" ? fr : en;
  const setText = lang === "fr" ? setFr : setEn;

  const generate = async (instruction) => {
    setGenBusy(true);
    try {
      const payload = { answers, instruction: instruction || "" };
      if (instruction && fr.trim()) payload.base_text = fr;
      const { data } = await api.post("/business/sales-policy/generate", payload);
      setFr(data.content.fr || "");
      setEn(data.content.en || "");
      toast.success("Brouillon généré ! Relisez et ajustez si besoin.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Génération impossible pour le moment.");
    } finally {
      setGenBusy(false);
    }
  };

  const save = async (pub) => {
    setSaveBusy(true);
    try {
      const { data } = await api.put("/business/sales-policy", { answers, content: { fr, en }, published: pub });
      setPublished(!!data.published);
      toast.success(pub ? "Règles publiées sur votre boutique ✅" : "Brouillon enregistré.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Enregistrement impossible.");
    } finally {
      setSaveBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-black/5 shadow-sm p-5 mt-4" data-testid="sales-policy-card">
      <h3 className="font-display font-semibold mb-1 flex items-center gap-2">
        <ScrollText className="h-5 w-5 text-[#1B4332]" /> Règles de vente
        {published && (
          <span data-testid="policy-published-badge" className="ml-1 inline-flex items-center gap-1 text-[11px] font-semibold text-[#1B4332] bg-[#1B4332]/10 px-2 py-0.5 rounded-full">
            <CheckCircle2 className="h-3 w-3" /> Publié
          </span>
        )}
      </h3>
      <p className="text-xs text-[#9c968a] mb-4">
        Répondez à quelques questions, l'IA rédige vos règles de vente. Relisez, ajustez, puis publiez-les sur votre boutique. Un lien discret s'affichera sous le bouton de commande.
      </p>

      <div className="space-y-3 mb-4">
        {QUESTIONS.map((q) => (
          <div key={q.key}>
            <label className="block text-sm font-semibold text-[#5C574F] mb-1">{q.label}</label>
            <textarea
              data-testid={`policy-q-${q.key}`}
              value={answers[q.key] || ""}
              onChange={(e) => setA(q.key, e.target.value)}
              placeholder={q.ph}
              rows={2}
              className="w-full bg-[#F9F6F0] rounded-xl p-3 text-sm outline-none border border-transparent focus:border-[#E85D04] resize-none"
            />
          </div>
        ))}
      </div>

      <button
        data-testid="policy-generate-btn"
        onClick={() => generate("")}
        disabled={genBusy || !hasAnswers}
        className="w-full h-12 rounded-2xl bg-[#1B4332] text-white font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50"
      >
        {genBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
        {genBusy ? "Génération en cours…" : (fr.trim() ? "Régénérer avec l'IA" : "Générer mes règles avec l'IA")}
      </button>

      {(fr.trim() || en.trim()) && (
        <div className="mt-4" data-testid="policy-editor">
          <div className="flex items-center justify-between mb-2">
            <div className="flex bg-[#F9F6F0] rounded-lg p-0.5 border border-[#E5E0D8]">
              {[["fr", "Français"], ["en", "English"]].map(([code, lbl]) => (
                <button
                  key={code}
                  type="button"
                  data-testid={`policy-lang-${code}`}
                  onClick={() => setLang(code)}
                  className={`px-3 h-8 rounded-md text-xs font-semibold transition-colors ${lang === code ? "bg-white text-[#1A1A1A] shadow-sm" : "text-[#9c968a]"}`}
                >
                  {lbl}
                </button>
              ))}
            </div>
            <button
              type="button"
              data-testid="policy-shorten-btn"
              onClick={() => generate("Raccourcis et simplifie ces règles, garde l'essentiel en un texte plus concis.")}
              disabled={genBusy || !fr.trim()}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#E85D04] disabled:opacity-40"
            >
              <Wand2 className="h-3.5 w-3.5" /> Raccourcir
            </button>
          </div>
          <textarea
            data-testid={`policy-text-${lang}`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={9}
            className="w-full bg-[#F9F6F0] rounded-xl p-3 text-sm outline-none border border-transparent focus:border-[#E85D04] resize-y leading-relaxed"
          />
          <p className="text-[11px] text-[#9c968a] mt-1">Vous pouvez modifier librement ce texte. La version anglaise est utilisée automatiquement pour les clients en anglais.</p>

          <div className="grid grid-cols-2 gap-2 mt-4">
            <button
              data-testid="policy-save-draft-btn"
              onClick={() => save(false)}
              disabled={saveBusy}
              className="h-12 rounded-2xl border border-[#E5E0D8] text-[#5C574F] font-semibold active:scale-95 transition-transform disabled:opacity-60"
            >
              Enregistrer le brouillon
            </button>
            <button
              data-testid="policy-publish-btn"
              onClick={() => save(true)}
              disabled={saveBusy || !fr.trim()}
              className="h-12 rounded-2xl bg-[#E85D04] text-white font-bold active:scale-95 transition-transform disabled:opacity-50"
            >
              {published ? "Mettre à jour" : "Publier"}
            </button>
          </div>
          {published && (
            <button
              data-testid="policy-unpublish-btn"
              onClick={() => save(false)}
              disabled={saveBusy}
              className="w-full h-10 mt-2 rounded-xl text-sm font-semibold text-[#D00000] disabled:opacity-60"
            >
              Dépublier (masquer de la boutique)
            </button>
          )}
        </div>
      )}
    </div>
  );
}
